/**
 * Lightweight first-party analytics (~1 KB).
 *
 * Events are queued in memory and flushed every 5 s (or on
 * visibilitychange) via `navigator.sendBeacon()` to a Lambda
 * Function URL read from `VITE_ANALYTICS_URL`.
 */

import { useEffect } from 'react';

/* ------------------------------------------------------------------ */
/*  Initial-search capture                                            */
/* ------------------------------------------------------------------ */

// DA-Round-1+2 fix: capture window.location.search SYNCHRONOUSLY at
// module-import time, BEFORE React Router can rewrite history.
// Reddit click attribution depends on reading raw rdt_cid from the URL.
// Uses `let` (not `const`) so __resetForTest can re-capture between test
// cases (and so this can be re-read if main.tsx imports analytics.ts
// before createBrowserRouter, per the import-ordering note in Task 10).
let initialSearch: string = '';

function captureInitialSearch(): void {
  if (typeof window === 'undefined') {
    initialSearch = '';
    return;
  }
  initialSearch = window.location.search;
}

// Run capture eagerly at module-import time.
captureInitialSearch();

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface AnalyticsEvent {
  event: string;
  properties: Record<string, string | number | boolean>;
  visitor_id: string;
  user_id?: string;
  timestamp: string; // ISO-8601
}

/* ------------------------------------------------------------------ */
/*  State                                                             */
/* ------------------------------------------------------------------ */

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 50;
const LS_KEY = 'pullit_visitor_id';

let queue: AnalyticsEvent[] = [];
let userId: string | undefined;
let timerId: ReturnType<typeof setInterval> | undefined;
let initialized = false;
let visibilityHandler: (() => void) | null = null;

/* ------------------------------------------------------------------ */
/*  Visitor ID                                                        */
/* ------------------------------------------------------------------ */

function resolveVisitorId(): string {
  // During SSG/prerender there is no usable identity; return a stable
  // ephemeral string so any module-top consumer doesn't crash. The real
  // id is resolved on the client after hydration via the same module
  // (module re-evaluated in the browser bundle, where crypto + localStorage
  // are both available).
  if (typeof window === 'undefined') return 'ssr-ephemeral';
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return stored;
    const id = crypto.randomUUID();
    localStorage.setItem(LS_KEY, id);
    return id;
  } catch {
    try {
      return crypto.randomUUID();
    } catch {
      return 'ssr-ephemeral';
    }
  }
}

let visitorId: string = resolveVisitorId();

const RDT_CID_KEY = 'pullit_rdt_cid';

function captureRedditClickId(): void {
  try {
    const params = new URLSearchParams(initialSearch);
    const cid = params.get('rdt_cid');
    if (cid) localStorage.setItem(RDT_CID_KEY, cid);
  } catch {
    // ignore
  }
}

export function getRedditClickId(): string | null {
  try {
    return localStorage.getItem(RDT_CID_KEY);
  } catch {
    return null;
  }
}

/**
 * SSR-safe hook that runs Reddit click-id capture once on the client.
 * Mounted from RootLayout; replaces the previous module-top side effect.
 */
export function useCaptureRedditClickId(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    captureRedditClickId();
  }, []);
}

/* ------------------------------------------------------------------ */
/*  Endpoint                                                          */
/* ------------------------------------------------------------------ */

function getEndpoint(): string {
  try {
    return (import.meta.env['VITE_ANALYTICS_URL'] as string) ?? '';
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/** Queue an analytics event. */
declare const rdt: ((...args: unknown[]) => void) | undefined;

const REDDIT_EVENT_MAP: Record<string, string> = {
  sign_up: 'SignUp',
  purchase_completed: 'Purchase',
  checkout_started: 'AddToCart',
};

export function track(
  event: string,
  properties: Record<string, string | number | boolean> = {},
): void {
  const entry: AnalyticsEvent = {
    event,
    properties,
    visitor_id: visitorId,
    timestamp: new Date().toISOString(),
  };
  if (userId !== undefined) {
    entry.user_id = userId;
  }
  queue.push(entry);

  // Fire Reddit Pixel conversion event if mapped
  const rdtEvent = REDDIT_EVENT_MAP[event];
  if (rdtEvent && typeof rdt === 'function') {
    // Canonical conversion ID — must match the server-side CAPI emit so Reddit
    // deduplicates the Pixel + CAPI fires as one conversion.
    // Purchase  → uses order_id.   AddToCart → uses idempotency_key.   SignUp → never fires from Pixel.
    const anchor =
      rdtEvent === 'AddToCart'
        ? (properties['idempotency_key'] as string | undefined)
        : (properties['order_id'] as string | undefined);

    const conversionId = anchor
      ? `${rdtEvent}-${anchor}`
      : `${visitorId}-${event}-${Date.now()}`;

    const rdtData: Record<string, unknown> = {
      conversionId,
      externalId: `${visitorId}-${event}-${Date.now()}`,
    };
    const clickId = getRedditClickId();
    if (clickId) rdtData['clickId'] = clickId;
    rdt('track', rdtEvent, rdtData);
  }
}

/** Flush up to MAX_BATCH queued events via sendBeacon. */
export function flush(): void {
  if (queue.length === 0) return;

  const endpoint = getEndpoint();
  if (!endpoint) {
    queue = [];
    return;
  }

  const batch = queue.splice(0, MAX_BATCH);

  try {
    fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(batch),
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Non-blocking: silently drop on failure
  }
}

/** Start the flush interval and attach the visibilitychange listener. */
export function init(): void {
  if (initialized) return;
  initialized = true;

  timerId = setInterval(flush, FLUSH_INTERVAL_MS);

  visibilityHandler = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('visibilitychange', visibilityHandler);
}

/** Associate a logged-in user with subsequent events. */
export function setUserId(id: string | undefined): void {
  userId = id;
}

/** Return the anonymous visitor UUID. */
export function getVisitorId(): string {
  return visitorId;
}

/* ------------------------------------------------------------------ */
/*  Test helpers (tree-shaken in prod)                                */
/* ------------------------------------------------------------------ */

/** @internal — reset all state for test isolation. */
export function __resetForTest(): void {
  queue = [];
  userId = undefined;
  if (timerId !== undefined) clearInterval(timerId);
  timerId = undefined;
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  initialized = false;
  visitorId = resolveVisitorId();
  captureInitialSearch();
}

/** @internal — expose the queue for assertions. */
export function __getQueue(): AnalyticsEvent[] {
  return queue;
}
// analytics endpoint configured
